import type { CustomComponent } from './plugin-loader.js';
import { PluginLoader } from './plugin-loader.js';
import { PluginDiscoveryService } from './plugin-discovery.js';
import type { PluginMetadata } from './plugin-metadata.js';
import * as path from 'path';
import * as fs from 'fs/promises';
import { invalidateAllCaches } from './cache-events.js';

export class PluginRegistry {
  private static instance: PluginRegistry;
  private plugins: Map<string, CustomComponent> = new Map();
  private pluginPaths: Map<string, string> = new Map();
  private pluginMetadata: Map<string, PluginMetadata> = new Map();
  private loader: PluginLoader;
  private discoveryService: PluginDiscoveryService;
  private _format?: 'docx' | 'pptx';
  /**
   * Identity of the last successfully loaded plugin set (names + paths +
   * mtimes + sizes). A repeat load of an identical set is skipped entirely —
   * the playground fires `/load-plugins` many times around page load, and
   * each used to re-import every plugin and clear every cache, resetting
   * cache stats before they could mean anything (#156).
   */
  private lastLoadFingerprint: string | null = null;
  private discoverAndLoadInFlight: Promise<{
    discovered: number;
    loaded: number;
  }> | null = null;

  private constructor() {
    this.loader = new PluginLoader();
    this.discoveryService = new PluginDiscoveryService();
  }

  static getInstance(): PluginRegistry {
    if (!PluginRegistry.instance) {
      PluginRegistry.instance = new PluginRegistry();
    }
    return PluginRegistry.instance;
  }

  setFormat(format: 'docx' | 'pptx'): void {
    this._format = format;
  }

  private notifyCacheInvalidation(): void {
    try {
      invalidateAllCaches();
    } catch {}
  }

  async loadPlugin(pathOrName: string): Promise<void> {
    try {
      await this.loader.initialize();

      const pluginPath = await this.resolvePluginPath(pathOrName);
      if (!pluginPath) {
        throw new Error(`Plugin '${pathOrName}' not found`);
      }

      const module = await this.loader.loadPlugin(pluginPath);
      if (module) {
        this.plugins.set(module.name, module);
        this.pluginPaths.set(module.name, pluginPath);
        // An explicit single-plugin load changes the set: the batch
        // fingerprint no longer describes it.
        this.lastLoadFingerprint = null;
        this.notifyCacheInvalidation();
      } else {
        throw new Error(`Failed to load valid component from ${pluginPath}`);
      }
    } catch (error: any) {
      throw new Error(
        `Failed to load plugin '${pathOrName}': ${error.message}`
      );
    }
  }

  async loadPlugins(pathsOrNames: string[]): Promise<void> {
    const errors: string[] = [];

    try {
      for (const pathOrName of pathsOrNames) {
        try {
          await this.loadPlugin(pathOrName);
        } catch (error: any) {
          errors.push(error.message);
        }
      }

      if (errors.length > 0) {
        throw new Error(
          `Failed to load ${errors.length} plugin(s): ${errors.join('; ')}`
        );
      }
    } finally {
      this.loader.cleanup();
    }
  }

  /**
   * Fingerprint a discovered plugin set by content identity: name, path,
   * mtime, size. Metadata alone can't tell an edited file from an unchanged
   * one, and re-importing is exactly what must be skipped for unchanged sets.
   */
  private async computeLoadFingerprint(
    pluginMetadata: PluginMetadata[]
  ): Promise<string> {
    const parts = await Promise.all(
      pluginMetadata.map(async (metadata) => {
        try {
          const stat = await fs.stat(metadata.filePath);
          return `${metadata.name}\0${metadata.filePath}\0${stat.mtimeMs}\0${stat.size}`;
        } catch {
          // Unreadable file: fingerprint it as such so a repeat load retries.
          return `${metadata.name}\0${metadata.filePath}\0unstat\0${Date.now()}`;
        }
      })
    );
    return parts.sort().join('');
  }

  private async loadPluginsFromMetadata(
    pluginMetadata: PluginMetadata[]
  ): Promise<number> {
    // Unchanged set already loaded → skip the re-import AND the cache
    // invalidation. Repeated `/load-plugins` calls stop churning cache
    // stats (#156).
    const fingerprint = await this.computeLoadFingerprint(pluginMetadata);
    if (
      fingerprint === this.lastLoadFingerprint &&
      pluginMetadata.every((metadata) => this.plugins.has(metadata.name))
    ) {
      return pluginMetadata.length;
    }

    let loadedCount = 0;

    for (const metadata of pluginMetadata) {
      try {
        const pluginPath = metadata.filePath;
        const module = await this.loader.loadPlugin(pluginPath);
        if (module) {
          this.plugins.set(module.name, module);
          this.pluginPaths.set(module.name, pluginPath);
          this.pluginMetadata.set(module.name, metadata);
          loadedCount++;
        }
      } catch {}
    }

    // ONE invalidation per batch, not one per plugin — a 10-plugin load used
    // to clear the document cache 10 times in a row.
    if (loadedCount > 0) {
      this.lastLoadFingerprint =
        loadedCount === pluginMetadata.length ? fingerprint : null;
      this.notifyCacheInvalidation();
    }

    return loadedCount;
  }

  async loadPluginsFromDirectory(dir: string): Promise<number> {
    try {
      await this.loader.initialize();

      const discovery = new PluginDiscoveryService({
        scope: dir,
        maxDepth: 5,
      });

      const pluginMetadata = await discovery.discover(this._format);
      return await this.loadPluginsFromMetadata(pluginMetadata);
    } catch (error: any) {
      throw new Error(
        `Failed to load plugins from directory: ${error.message}`
      );
    } finally {
      this.loader.cleanup();
    }
  }

  /**
   * Concurrent callers coalesce into one discovery pass: the playground's
   * bootstrap POST /load-plugins and on-demand schema-generation loads race
   * on page load, and each used to run its own discovery walk and re-import
   * every plugin.
   */
  async discoverAndLoad(): Promise<{ discovered: number; loaded: number }> {
    if (!this.discoverAndLoadInFlight) {
      this.discoverAndLoadInFlight = this.performDiscoverAndLoad().finally(
        () => {
          this.discoverAndLoadInFlight = null;
        }
      );
    }
    return this.discoverAndLoadInFlight;
  }

  private async performDiscoverAndLoad(): Promise<{
    discovered: number;
    loaded: number;
  }> {
    try {
      await this.loader.initialize();

      const pluginMetadata = await this.discoveryService.discover(this._format);
      const discovered = pluginMetadata.length;

      if (discovered === 0) {
        return { discovered: 0, loaded: 0 };
      }

      const loaded = await this.loadPluginsFromMetadata(pluginMetadata);
      return { discovered, loaded };
    } catch (error: any) {
      throw new Error(`Failed to discover and load plugins: ${error.message}`);
    } finally {
      this.loader.cleanup();
    }
  }

  async resolvePluginName(name: string): Promise<string | undefined> {
    if (this.pluginPaths.has(name)) {
      return this.pluginPaths.get(name);
    }

    try {
      const pluginMetadata = await this.discoveryService.discover(this._format);
      const metadata = pluginMetadata.find((p) => p.name === name);
      if (metadata) return metadata.filePath;
    } catch {}

    return undefined;
  }

  private async resolvePluginPath(
    pathOrName: string
  ): Promise<string | undefined> {
    if (pathOrName.endsWith('.ts') || pathOrName.endsWith('.js')) {
      return path.resolve(process.cwd(), pathOrName);
    }
    return await this.resolvePluginName(pathOrName);
  }

  getPlugins(): CustomComponent[] {
    return Array.from(this.plugins.values());
  }

  getPluginNames(): string[] {
    return Array.from(this.plugins.keys());
  }

  getPlugin(name: string): CustomComponent | undefined {
    return this.plugins.get(name);
  }

  hasPlugins(): boolean {
    return this.plugins.size > 0;
  }

  getPluginCount(): number {
    return this.plugins.size;
  }

  clear(): void {
    this.plugins.clear();
    this.pluginPaths.clear();
    this.pluginMetadata.clear();
    this.lastLoadFingerprint = null;
    this.loader.cleanup();
    this.notifyCacheInvalidation();
  }

  static cleanup(): void {
    if (PluginRegistry.instance) {
      PluginRegistry.instance.clear();
    }
  }

  getPluginMetadata(name: string): PluginMetadata | undefined {
    return this.pluginMetadata.get(name);
  }

  getAllPluginMetadata(): PluginMetadata[] {
    return Array.from(this.pluginMetadata.values());
  }
}
